import { links } from '../links'
import type { Party } from './de'

/**
 * Englische Fassung der Carrier-Hub-Landingpage.
 *
 * TODO (Kunde): Übersetzung aus dem Deutschen – bitte fachlich und
 * terminologisch prüfen. Insbesondere die Rollennamen: „Abfrager" ist hier
 * „requester", „Anbieter" ist „provider", „GU-Partner" ist „GC partner"
 * (general contractor). Falls im Englischen andere Bezeichnungen etabliert
 * sind, nur hier austauschen.
 */

export const hero = {
  title: ['Less dark.', 'More fiber.'],
}

export const intro = {
  eyebrow: 'The central platform for dark fiber',
  title: ['Find dark fiber connections', 'across multiple networks.'],
  body: [
    'Carrier Hub brings regional and nationwide fiber networks together in one central route search.',
    'That gives you a quick first assessment of possible connections across several networks.',
    'The platform consolidates the route and address data supplied to it and makes it usable for a straightforward route search.',
  ],
  lead: 'One platform for both sides. Requesters look for local, regional or nationwide dark fiber connections. Providers contribute their network data securely to the route search and receive matching enquiries.',
  leadStrong: 'One platform for both sides.',
}

export const parties: { top: Party; left: Party; right: Party } = {
  top: {
    name: 'Requester',
    points: [
      { text: 'potential customer for a dark fiber connection' },
      { text: 'needs one or more routes', note: 'local/regional or nationwide' },
    ],
  },
  left: {
    name: 'GC partner',
    points: [
      {
        text: 'enters the network',
        note: 'nationwide / regional',
        suffix: 'including pricing parameters into the HUB',
      },
      { text: 'receives enquiries from the HUB', note: 'handles leads / quotes / orders' },
    ],
  },
  right: {
    name: 'Network partner',
    points: [
      { text: 'contributes its network directly to the HUB' },
      { text: 'marketing runs case by case via the GC' },
    ],
  },
}

export const audiences = [
  {
    id: 'abfrager',
    role: 'Requester',
    title: ['Looking for a local,', 'regional or nationwide', 'dark fiber connection?'],
    body: 'Enter the start and end point and receive a first assessment of possible routes.',
    cta: { label: 'Request a route', href: links.routeQuery, variant: 'primary' as const },
  },
  {
    id: 'anbieter',
    role: 'Provider',
    title: ['Want to open your network', 'up to additional', 'enquiries?'],
    body: 'Contribute your network and address data securely to Carrier Hub and be considered whenever a matching route is searched for.',
    cta: { label: 'Contribute a network', href: links.networkOffer, variant: 'secondary' as const },
  },
]

export const trust = {
  eyebrow: 'Trust & security',
  title: 'Your network data stays protected',
  cards: [
    {
      eyebrow: 'Protected data transfer',
      title: ['Secure', 'transmission'],
      body: 'Network and address data are delivered and updated via a secured SFTP server.',
    },
    {
      eyebrow: 'Human oversight instead of open access',
      title: ['Verification of', 'requesting parties'],
      body: 'Requesting parties complete a documented registration process before they can use the platform. Unusual query behaviour is blocked.',
    },
    {
      eyebrow: 'Security by design',
      title: ['Protected', 'processing'],
      body: 'vitroconnect is subject to KRITIS regulation, and we treat our partners’ data as we do our own. vitroconnect is also certified to ISO/IEC 27001.',
    },
    {
      eyebrow: 'No disclosure of your infrastructure',
      title: ['Risk-free', 'market representation'],
      body: 'Confidential geographical information is not shown precisely; access points and route alignments are deliberately displayed with reduced accuracy.',
    },
  ],
}

export const process = {
  eyebrow: 'How a route enquiry works',
  title: ['From the search', 'to a concrete enquiry.'],
  body: 'Enter the start and end point, review possible connections and take the enquiry forward through Carrier Hub. Feasibility and terms are then checked and a concrete quote is prepared.',
  actor: 'Requester',
  phases: [
    { label: 'Enquiry', width: '17.02%' },
    { label: 'Planning', width: '63.83%' },
    { label: 'Order', width: '17.02%' },
  ],
  steps: [
    {
      title: 'Search for a route',
      body: 'Enter the start and end point of the connection you need',
    },
    {
      title: 'Review the options',
      body: 'Receive possible end-to-end and alternative routes across the connected networks.',
    },
    {
      title: 'Take the enquiry forward',
      body: 'Save the route and hand it over to the responsible GC partner for further review and a quote.',
    },
  ],
}

export const together = {
  eyebrow: 'Carrier Hub for requesters and providers',
  title: ['Route demand meets network capacity.', 'Value for both sides.'],
  columns: [
    {
      role: 'Requester',
      cta: { label: 'Request a route', href: links.routeQuery, variant: 'primary' as const },
      benefits: [
        { text: 'Central enquiries instead of many individual ones', strong: 'Central enquiries' },
        { text: 'Fast first assessment of possible routes', strong: 'Fast first assessment' },
        { text: 'Less research and planning effort', strong: 'Less research' },
        { text: 'Bundled ordering with consistent terms and SLAs', strong: 'Bundled ordering' },
      ],
    },
    {
      role: 'Provider',
      cta: { label: 'Contribute a network', href: links.networkOffer, variant: 'secondary' as const },
      benefits: [
        { text: 'An additional digital sales channel', strong: 'sales channel' },
        { text: 'New qualified leads and orders', strong: 'qualified leads' },
        { text: 'Better utilisation of existing network infrastructure', strong: 'existing network infrastructure' },
        { text: 'Network data contributed securely to the route search', strong: 'Network data contributed securely' },
      ],
    },
  ],
}

export const faq = {
  title: 'FAQs',
  items: [
    {
      question: 'How does a route enquiry work?',
      answer:
        'The requester enters the start and end point. Based on the stored network data, Carrier Hub determines possible connections and shows alternatives. If a lead comes out of it, it is forwarded to the responsible GC partner for further review and a quote.',
    },
    // TODO (Kunde): Die folgenden vier Antworten liegen im Entwurf nicht vor;
    // die deutschen Fassungen sind abgeleitet, diese sind deren Übersetzung.
    {
      question: 'How are possible routes calculated?',
      answer:
        'The basis is the route and address data supplied by the network and GC partners, which Carrier Hub consolidates centrally. From that, possible end-to-end and alternative routes across the connected networks are determined and shown as a first assessment.',
    },
    {
      question: 'Is the route availability binding?',
      answer:
        'No. The result of the route search is a first assessment of possible connections. Feasibility and terms are then checked by the responsible GC partner; only the resulting quote is binding.',
    },
    {
      question: 'Will my network and route data be visible to other partners?',
      answer:
        'No. Confidential geographic information is not shown precisely and entry points are deliberately displayed with reduced accuracy. Data is delivered through a secured SFTP server, and access follows the need-to-know principle.',
    },
    {
      question: 'Who can see customer, enquiry and lead data?',
      answer:
        'Enquiries and leads are forwarded solely to the responsible GC partner. Administrative access requires MFA and clearly attributable accounts. Carrier Hub is subject to KRITIS and the vitroconnect ISMS; subcontractors are bound by the same rules.',
    },
  ],
}

export const finalCta = {
  eyebrow: 'Carrier Hub',
  title: 'Less dark. More fiber.',
  body: 'Start straight away with a route enquiry, or get in touch if you would like to contribute your network to Carrier Hub.',
  ctas: [
    { label: 'Request a route', href: links.routeQuery, variant: 'primary' as const },
    { label: 'Contribute a network', href: links.networkOffer, variant: 'secondary' as const },
  ],
}

export const footerLinks = [
  { label: 'CAP', href: links.cap },
  { label: 'Imprint', href: links.imprint },
  { label: 'Privacy', href: links.privacy },
]

export const ui = {
  skipToContent: 'Skip to content',
  toAudiences: 'To the offerings for requesters and providers',
}
